import { Panel } from "@/components/Panel";
import { EditableField } from "@/components/EditableField";
import { useCrossSelling } from "@/api/stockSubresources";
import type { StockItem, StockItemUpdatePayload } from "@/api/stock";

interface Props {
  sku: string;
  item: StockItem;
  save: <K extends keyof StockItemUpdatePayload>(
    key: K,
    value: StockItemUpdatePayload[K],
  ) => Promise<void>;
}

export function CrossSellingTab({ sku, item, save }: Props) {
  const { data: rows = [], isLoading } = useCrossSelling(sku);

  return (
    <div className="flex flex-col gap-4">
      <Panel title="Cross-Selling — Konfiguration">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <EditableField
            type="boolean"
            label="Cross-Selling aktiv"
            value={item.cross_selling_active ?? false}
            onSave={(v) => save("cross_selling_active", v as boolean)}
          />
          <EditableField
            type="boolean"
            label="Mengen-Split"
            value={item.cross_selling_quantity_split ?? false}
            onSave={(v) => save("cross_selling_quantity_split", v as boolean)}
          />
          <EditableField
            type="number"
            mono
            label="Beschreibung National (ID)"
            value={item.cross_selling_desc_national_id}
            onSave={(v) => save("cross_selling_desc_national_id", v as number)}
          />
          <EditableField
            type="number"
            mono
            label="Beschreibung International (ID)"
            value={item.cross_selling_desc_intl_id}
            onSave={(v) => save("cross_selling_desc_intl_id", v as number)}
          />
        </div>
      </Panel>

      <Panel
        title="Verknuepfte Cross-Selling-Items"
        subtitle={`${rows.length} Einträge`}
      >
        {isLoading ? (
          <div className="text-sm text-muted">Lade Cross-Selling-Items…</div>
        ) : rows.length === 0 ? (
          <div className="text-center text-sm text-muted py-6">
            Keine Cross-Selling-Verknuepfungen.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-muted text-left">
                <th className="pb-2">Related DR ID</th>
                <th className="pb-2">Folder Path</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-2">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-panel-2/40">
                  <td className="py-2 font-mono text-xs">
                    {r.related_dr_id ?? <span className="text-muted-2 italic">—</span>}
                  </td>
                  <td className="py-2 text-xs text-ink-2">
                    {r.folder_path ?? <span className="text-muted-2 italic">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
